import React, { useState, useRef, useEffect } from 'react';
import { Send, Reply, X, MessageSquarePlus, MoreHorizontal, Edit2, Trash2, ChevronDown } from 'lucide-react';
import { useComments, useAddComment, useEditComment, useDeleteComment } from '../hooks/use-panels';
import { useAuth } from '../../../contexts/AuthContext';
import { timeAgo } from '../../../utils/timeAgo';
import CoFounderBadge from '../../../components/ui/CoFounderBadge';
import { isCoFounder } from '../../../utils/cofounderBadge';

const DiscussionComments = ({ panelId, discussionId }) => {
  const { user } = useAuth();
  const [commentContent, setCommentContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [replyingTo, setReplyingTo] = useState(null); // comment id
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState(new Set()); // Set of comment IDs with expanded replies
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // ID of comment to delete
  
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useComments(panelId, discussionId);

  const { mutate: addComment, isPending: isAdding } = useAddComment();
  const { mutate: editComment, isPending: isEditing } = useEditComment();
  const { mutate: deleteComment, isPending: isDeleting } = useDeleteComment();

  // Close menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  // Flatten the pages array into a single array of comments
  const comments = data?.pages.flatMap((page) => page.data) || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = commentContent.trim();
    if (!trimmed || trimmed.length > 300 || !panelId || !discussionId) return;

    addComment(
      { panelId, discussionId, content: trimmed },
      { onSuccess: () => setCommentContent('') }
    );
  };

  const handleReplySubmit = (e, parentId) => {
    e.preventDefault();
    const trimmed = replyContent.trim();
    if (!trimmed || trimmed.length > 300 || !panelId || !discussionId) return;

    addComment(
      { panelId, discussionId, content: trimmed, parentId },
      { onSuccess: () => { setReplyContent(''); setReplyingTo(null); } }
    );
  };

  const handleEditSave = (e, commentId) => {
    e.preventDefault();
    const trimmed = editContent.trim();
    if (!trimmed || trimmed.length > 300) return;

    editComment(
      { panelId, discussionId, commentId, content: trimmed },
      { onSuccess: () => { setEditingCommentId(null); setEditContent(''); } }
    );
  };

  const handleDeleteClick = (commentId) => {
    setConfirmDeleteId(commentId);
    setOpenMenuId(null);
  };

  const confirmDelete = () => {
    if (confirmDeleteId) {
      deleteComment({ panelId, discussionId, commentId: confirmDeleteId });
      setConfirmDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const handleReplyClick = (comment) => {
    setReplyingTo(comment.id);
    setReplyContent('');
    setEditingCommentId(null);
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 50);
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setReplyContent('');
  };

  const toggleReplies = (commentId) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  };

  const getAvatar = (name) => {
    const initials = (name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
    const colors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500'];
    const colorIndex = (name || '').length % colors.length;
    return { initials, colorClass: colors[colorIndex] };
  };

  const renderComment = (comment, isReply = false, topLevelId = null) => {
    const avatar = getAvatar(comment.author?.name || user?.name);
    const isOwner = (user?.id && (user.id === comment.authorId || user.id === comment.author?.id)) || (user?.name && (comment.author?.name === user.name || !comment.author?.name));
    const isEdited = new Date(comment.updatedAt).getTime() - new Date(comment.createdAt).getTime() > 1000;
    const isEditingThis = editingCommentId === comment.id;
    const currentTopLevelId = topLevelId || comment.id;
    const replyCount = comment._count?.replies ?? comment.replies?.length ?? 0;
    const repliesExpanded = expandedReplies.has(comment.id);

    // Soft-deleted tombstone
    if (comment.isDeleted) {
      return (
        <div key={comment.id} className={`flex gap-3 ${isReply ? 'ml-8' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-gray-400 text-xs">?</span>
          </div>
          <div className="flex-1 bg-gray-50 border border-dashed border-gray-200 rounded-xl p-3">
            <p className="text-xs text-gray-400 italic">[Comment deleted]</p>
          </div>
        </div>
      );
    }

    return (
      <div key={comment.id} className={`flex gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100 min-w-0 ${isReply ? 'ml-8 bg-gray-50/50' : ''}`}>
        <div className={`w-8 h-8 rounded-full ${avatar.colorClass} flex items-center justify-center text-white text-xs font-semibold shrink-0 mt-0.5 overflow-hidden border border-indigo-100`}>
          {comment.author?.avatarUrl || (isOwner && user?.avatarUrl) ? (
            <img src={comment.author?.avatarUrl || user?.avatarUrl} alt={comment.author?.name || user?.name} className="w-full h-full object-cover" />
          ) : (
            avatar.initials
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-[13px] truncate">
                {comment.author?.role === 'student' || (isOwner && user?.role === 'student')
                  ? (comment.author?.name || (isOwner ? user?.name : 'Student'))
                  : isCoFounder(comment.author) || (isOwner && isCoFounder(user))
                    ? (comment.author?.name || (isOwner ? user?.name : 'Unknown'))
                    : (comment.author?.name ? (comment.author?.name.startsWith('Dr.') ? comment.author.name : `Dr. ${comment.author.name}`) : (isOwner && user?.name ? `Dr. ${user.name}` : 'Unknown User'))}
              </span>
              {(comment.author?.jobRole || (isOwner && user?.jobRole) || comment.author?.role === 'student' || (isOwner && user?.role === 'student')) && (
                <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-indigo-100 shrink-0">
                  {comment.author?.jobRole || (isOwner ? user?.jobRole : null) || (comment.author?.role === 'student' || (isOwner && user?.role === 'student') ? 'Student' : (comment.author?.specialization || 'Veterinarian'))}
                </span>
              )}
              <span className="text-gray-400 text-[11px] shrink-0">•</span>
              <span className="text-gray-400 text-[11px] shrink-0 flex items-center gap-1">
                {timeAgo(comment.createdAt)}
                {isEdited && <span className="italic opacity-80">(edited)</span>}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {!isReply && (
                <button 
                  onClick={() => handleReplyClick(comment)}
                  className="text-xs text-gray-400 hover:text-[#6b46c1] flex items-center gap-1 transition-colors font-medium"
                >
                  <Reply size={13} /> Reply
                </button>
              )}
              {isOwner && (
                <div className="relative" ref={openMenuId === comment.id ? menuRef : null}>
                  <button 
                    onClick={() => setOpenMenuId(openMenuId === comment.id ? null : comment.id)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <MoreHorizontal size={14} />
                  </button>
                  {openMenuId === comment.id && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10 animate-in fade-in zoom-in-95 duration-100">
                      <button
                        onClick={() => {
                          setEditingCommentId(comment.id);
                          setEditContent(comment.content);
                          setOpenMenuId(null);
                          setReplyingTo(null);
                        }}
                        className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Edit2 size={13} className="text-gray-500" /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(comment.id)}
                        disabled={isDeleting}
                        className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                      >
                        <Trash2 size={13} className="text-red-500" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Comment Content / Edit Mode */}
          {isEditingThis ? (
            <form onSubmit={(e) => handleEditSave(e, comment.id)} className="mt-2">
              <textarea
                value={editContent}
                maxLength={300}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6b46c1]/20 focus:border-[#6b46c1] min-h-[60px] resize-y break-words [overflow-wrap:anywhere]"
                disabled={isEditing}
                autoFocus
              />
              <div className="flex items-center justify-between mt-1">
                <span className={`text-[11px] font-medium transition-colors ${
                  editContent.length >= 300
                    ? 'text-red-500 font-bold'
                    : editContent.length >= 270
                    ? 'text-amber-500 font-semibold'
                    : 'text-gray-400'
                }`}>
                  {editContent.length}/300
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingCommentId(null)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    disabled={isEditing}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-xs font-medium text-white bg-[#6b46c1] hover:bg-[#553098] rounded-md transition-colors disabled:opacity-50"
                    disabled={!editContent.trim() || isEditing || editContent.length > 300}
                  >
                    {isEditing ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <p className="text-gray-700 text-sm whitespace-pre-wrap break-words break-all [overflow-wrap:anywhere] leading-relaxed mt-1">
              {comment.content}
            </p>
          )}

          {/* Collapsed Replies Toggle */}
          {!isReply && replyCount > 0 && (
            <button
              onClick={() => toggleReplies(comment.id)}
              className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#6b46c1] hover:text-[#553098] transition-colors"
            >
              <ChevronDown
                size={13}
                className={`transition-transform duration-200 ${repliesExpanded ? 'rotate-180' : ''}`}
              />
              {repliesExpanded ? 'Hide replies' : `View ${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
            </button>
          )}

          {/* Nested Replies — only shown when expanded */}
          {!isReply && repliesExpanded && comment.replies && comment.replies.length > 0 && (
            <div className="mt-3 space-y-3">
              {comment.replies.map(reply => renderComment(reply, true, currentTopLevelId))}
            </div>
          )}

          {/* Inline Reply Form */}
          {replyingTo === comment.id && !isEditingThis && (
            <div className="mt-3">
              <form onSubmit={(e) => handleReplySubmit(e, currentTopLevelId)} className="flex flex-col gap-1.5">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={replyContent}
                    maxLength={300}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder={`Replying to ${comment.author?.name || 'Unknown User'}... (max 300 chars)`}
                    className="flex-1 px-4 py-2 bg-white border border-indigo-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#6b46c1]/20 focus:border-[#6b46c1] transition-all"
                    disabled={isAdding || isLoading}
                  />
                  <button
                    type="submit"
                    disabled={!replyContent.trim() || isAdding || isLoading || replyContent.length > 300}
                    className="p-2 bg-[#6b46c1] text-white rounded-xl hover:bg-[#553098] transition-colors disabled:opacity-50 flex items-center justify-center"
                  >
                    <Send size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={cancelReply}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
                {replyContent.length > 0 && (
                  <div className="flex justify-end pr-1">
                    <span className={`text-[11px] font-medium transition-colors ${
                      replyContent.length >= 300
                        ? 'text-red-500 font-bold'
                        : replyContent.length >= 270
                        ? 'text-amber-500 font-semibold'
                        : 'text-gray-400'
                    }`}>
                      {replyContent.length}/300
                    </span>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      {/* Top Input Form for standard comments */}
      <div className="mb-6">
        <form onSubmit={handleSubmit} className="flex flex-col gap-1.5">
          <div className="flex gap-2">
            <input
              type="text"
              value={commentContent}
              maxLength={300}
              onChange={(e) => setCommentContent(e.target.value)}
              placeholder="Write a comment... (max 300 chars)"
              className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#6b46c1]/20 focus:border-[#6b46c1] transition-all"
              disabled={isAdding || isLoading}
            />
            <button
              type="submit"
              disabled={!commentContent.trim() || isAdding || isLoading || commentContent.length > 300}
              className="px-4 bg-[#6b46c1] text-white hover:bg-[#553098] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center rounded-xl font-medium text-sm"
            >
              Post
            </button>
          </div>
          {commentContent.length > 0 && (
            <div className="flex justify-end pr-1">
              <span className={`text-[11px] font-medium transition-colors ${
                commentContent.length >= 300
                  ? 'text-red-500 font-bold'
                  : commentContent.length >= 270
                  ? 'text-amber-500 font-semibold'
                  : 'text-gray-400'
              }`}>
                {commentContent.length}/300
              </span>
            </div>
          )}
        </form>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#6b46c1]" />
        </div>
      ) : isError ? (
        <p className="text-sm text-red-500 text-center py-4">Error loading comments.</p>
      ) : comments.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-gray-200 rounded-xl">
          <MessageSquarePlus size={20} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No comments yet. Be the first to start the conversation!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => renderComment(comment))}
          
          {/* Load More Button */}
          {hasNextPage && (
            <div className="text-center pt-2">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="text-sm font-semibold text-[#6b46c1] hover:underline disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading...' : 'Load more comments'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Comment</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this comment? This action cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-lg transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscussionComments;
